import { type FormEvent, useCallback, useState } from "react";

import { trpc } from "@blis/trpc/react";

import { Button } from "@/components/ui/button";
import { FieldError, Input, TextField } from "@/components/ui/field";
import { Form } from "@/components/ui/form";
import { PasswordInput } from "@/components/ui/password-input";
import { Text } from "@/components/ui/text";
import type { TAuthProvider } from "@/lib/types";
import { AuthLayout } from "./AuthLayout";

const loginPath = "/api/auth/login";

export function Login({ next }: { next?: string }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [authProvider, setAuthProvider] = useState<TAuthProvider | null>(null);

  const utils = trpc.useUtils();

  const onLogin = useCallback(
    async (evt: FormEvent<HTMLFormElement>) => {
      evt.preventDefault();

      const formData = new FormData(evt.currentTarget);
      const values = Object.fromEntries(formData) as {
        email: string;
        password?: string;
      };

      try {
        setError("");
        setLoading(true);
        let providerType = authProvider?.type;
        if (!providerType) {
          const provider = await utils.anon.authProvider.fetch(values.email, {
            retry: 1,
          });
          providerType = provider.type;
          setAuthProvider(provider);

          if (providerType === "password") {
            setLoading(false);
            // return early to capture password
            return;
          }
        }

        if (providerType === "password") {
          // Handle password authentication with fetch to get JSON error responses
          const response = await fetch("/api/auth/login", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              email: values.email,
              password: values.password,
            }),
          });

          if (response.ok) {
            // Success - redirect to the intended destination
            const returnTo = next || "/";
            window.location.href = returnTo;
          } else if (response.status === 401) {
            // Authentication failed - show error inline
            const errorData = await response.json();
            setError(errorData.message || "Invalid email or password");
            setLoading(false);
          } else {
            // Other errors
            setError("Something went wrong. Please try again.");
            setLoading(false);
          }
        } else if (providerType === "sso") {
          const returnTo = `${window.location.origin}${next || "/"}`;
          const url = new URL(loginPath, window.location.origin);
          url.searchParams.append("returnTo", returnTo);
          url.searchParams.append("email", values.email);
          window.location.href = url.toString();
        } else if (providerType === "email") {
          const returnTo = `${window.location.origin}${next || "/"}`;
          const url = new URL(loginPath, window.location.origin);
          url.searchParams.append("returnTo", returnTo);
          url.searchParams.append("email", values.email);
          window.location.href = url.toString();
        } else {
          setLoading(false);
          setError(
            "We're unable to sign you in. Please contact your administrator for assistance.",
          );
        }
      } catch (err) {
        setLoading(false);
        setError("Something went wrong. Please try again.");
        console.error(err);
      }
    },
    [authProvider?.type, utils.anon.authProvider, next],
  );

  return (
    <AuthLayout title="Sign in to bLIS">
      <Text muted>Enter your email below to continue</Text>
      <div className="flex flex-col w-full mt-6">
        {error && (
          <Text size="2" className="mb-2 text-destructive">
            {error}
          </Text>
        )}
        <Form onSubmit={onLogin}>
          <div className="flex flex-col gap-2">
            <TextField
              name="email"
              type="email"
              aria-label="Email address"
              isRequired
              onBlur={async (evt) => {
                const email = (evt.target as HTMLInputElement).value;
                if (authProvider && email) {
                  // reset auth provider if email changes
                  const provider = await utils.anon.authProvider.fetch(email, {
                    retry: false,
                  });
                  setAuthProvider(provider);
                }
              }}
            >
              <Input placeholder="Email address" />
              <FieldError />
            </TextField>

            {authProvider?.type === "password" && (
              <TextField
                name="password"
                isRequired
                autoFocus
                aria-label="Password"
              >
                <PasswordInput placeholder="Password" />
                <FieldError />
              </TextField>
            )}
            <Button type="submit" isPending={loading} isDisabled={loading}>
              Continue
            </Button>
          </div>
        </Form>
      </div>
    </AuthLayout>
  );
}
